// 057A — Escalas Geriátricas (Anexo 1 MSP, SNS-MSP/HCU-form.057A/2021). Fase 7, Tanda 7: las 5 escalas impresas
// (Lawton y Brody, Timed Get Up and Go, Pfeiffer, Yesavage, Barthel). Decisiones y catálogo en
// `escalasGeriatricasDatos.ts`. Encabezado por `EncabezadoMspCampos` (precedente 058A). Sin FHIR: quedan como `Par[]`
// en el PDF firmado.
import type { JSX } from 'react';
import { useState } from 'react';
import { useResource } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloque, CamposSimples, SelectSimple } from './camposSimples';
import { calcularTotalEscala, EscalaClinica } from './uci/EscalaClinica';
import {
  BANDAS_TUG,
  CAMPOS_TUG,
  CORTE_YESAVAGE,
  cortePfeiffer,
  DEFINICION_BARTHEL_057A,
  DEFINICION_YESAVAGE,
  definicionPfeiffer057A,
  estadoInicialEscalasGeriatricas,
  INSTRUCCIONES_TUG,
  ITEMS_LAWTON,
  NOTA_PFEIFFER,
  OPCIONES_LEER_ESCRIBIR,
  OPCIONES_SEXO_LAWTON,
  paresDeEscalasGeriatricas,
  sexoLawtonDePaciente,
  totalLawton,
  type EstadoEscalasGeriatricas,
} from './escalasGeriatricasDatos';

const CODIGO = '057A';

export function Formulario057A({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.057A');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);
  const [estado, setEstado] = useState<EstadoEscalasGeriatricas>(() => estadoInicialEscalasGeriatricas());
  const set = (parcial: Partial<EstadoEscalasGeriatricas>): void => setEstado({ ...estado, ...parcial });

  // El sexo sale de la ficha del paciente; solo si falta o no es hombre/mujer se pide en pantalla (no se asume).
  const paciente = useResource<Patient>(subject);
  const sexoPaciente = sexoLawtonDePaciente(paciente?.gender);
  const sexoLawton = sexoPaciente || estado.sexoLawton;
  const lawton = totalLawton(estado.lawton, sexoLawton);
  const defPfeiffer = definicionPfeiffer057A(estado.pfeifferSinTelefono);
  const totalPfeiffer = calcularTotalEscala(defPfeiffer, estado.pfeiffer);
  const corte = cortePfeiffer(estado.sabeLeerEscribir);
  const totalYesavage = calcularTotalEscala(DEFINICION_YESAVAGE, estado.yesavage);

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      onGuardado(paresDeEscalasGeriatricas({ ...estado, sexoLawton }), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f057a.lawtonTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f057a.lawtonNota')}</p>
        {sexoPaciente ? (
          <p className="text-sm text-hce-ink">{t('formularios.f057a.sexoLawtonDeFicha', { sexo: OPCIONES_SEXO_LAWTON.find((x) => x.valor === sexoPaciente)?.etiqueta ?? '' })}</p>
        ) : (
          <SelectSimple id="f057a-lawton-sexo" etiqueta={t('formularios.f057a.sexoLawton')} valor={estado.sexoLawton} opciones={OPCIONES_SEXO_LAWTON} onChange={(v) => set({ sexoLawton: v })} />
        )}
        {ITEMS_LAWTON.map((item) => (
          <SelectSimple
            key={item.clave}
            id={`f057a-lawton-${item.clave}`}
            etiqueta={item.etiqueta}
            valor={estado.lawton[item.clave] ?? ''}
            opciones={item.opciones.map((op, i) => ({ valor: String(i), etiqueta: `${op.etiqueta} (${op.puntos})` }))}
            onChange={(v) => set({ lawton: { ...estado.lawton, [item.clave]: v } })}
          />
        ))}
        <p className="text-sm font-medium text-hce-ink">{lawton ? t('formularios.f057a.lawtonTotal', { total: lawton.total, maximo: lawton.maximo }) : t('formularios.f057a.lawtonTotalPendiente')}</p>
      </Bloque>

      <Bloque titulo={t('formularios.f057a.tugTitulo')}>
        <p className="text-xs text-hce-muted">{INSTRUCCIONES_TUG}</p>
        <CamposSimples prefijoId="f057a-tug" campos={CAMPOS_TUG} valores={estado.tug} columnas={2} onChange={(v) => set({ tug: v })} />
        <p className="text-xs text-hce-muted">{t('formularios.f057a.tugBandas')}</p>
        <ul className="list-disc pl-5 text-xs text-hce-muted">
          {BANDAS_TUG.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </Bloque>

      <Bloque titulo={t('formularios.f057a.pfeifferTitulo')}>
        <p className="text-xs text-hce-muted">{NOTA_PFEIFFER}</p>
        <SelectSimple id="f057a-pfeiffer-lee" etiqueta={t('formularios.f057a.leerEscribir')} valor={estado.sabeLeerEscribir} opciones={OPCIONES_LEER_ESCRIBIR} onChange={(v) => set({ sabeLeerEscribir: v })} />
        <div className="flex items-center gap-2">
          <Checkbox id="f057a-pfeiffer-sin-telefono" checked={estado.pfeifferSinTelefono} onCheckedChange={(v) => set({ pfeifferSinTelefono: v === true })} />
          <Label htmlFor="f057a-pfeiffer-sin-telefono" className="font-normal">
            {t('formularios.f057a.sinTelefono')}
          </Label>
        </div>
        <EscalaClinica idPrefix="f057a-pfeiffer" definicion={defPfeiffer} respuestas={estado.pfeiffer} onChange={(r) => set({ pfeiffer: r })} />
        {totalPfeiffer !== undefined && corte !== undefined && (
          <p className="text-sm font-medium text-hce-ink">{totalPfeiffer >= corte ? t('formularios.f057a.pfeifferSospecha', { corte }) : t('formularios.f057a.pfeifferBajoCorte', { corte })}</p>
        )}
      </Bloque>

      <Bloque titulo={t('formularios.f057a.yesavageTitulo')}>
        <EscalaClinica idPrefix="f057a-yesavage" definicion={DEFINICION_YESAVAGE} respuestas={estado.yesavage} onChange={(r) => set({ yesavage: r })} />
        <p className="text-xs text-hce-muted">{t('formularios.f057a.yesavageCorte', { corte: CORTE_YESAVAGE })}</p>
        {totalYesavage !== undefined && totalYesavage >= CORTE_YESAVAGE && <p className="text-sm font-medium text-hce-ink">{t('formularios.f057a.yesavageDepresion')}</p>}
      </Bloque>

      <Bloque titulo={t('formularios.f057a.barthelTitulo')}>
        <EscalaClinica idPrefix="f057a-barthel" definicion={DEFINICION_BARTHEL_057A} respuestas={estado.barthel} onChange={(r) => set({ barthel: r })} />
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
